/**
 * 환경변수 접근을 한 곳으로 모아 기본값/누락 처리를 통일한다.
 * 값을 직접 process.env에서 읽지 말고 이 모듈을 거친다.
 *
 * ⚠️ NEXT_PUBLIC_* 는 런타임 env 가 아니라 `next build` 시점에 번들에 인라인된다.
 * 여기 값들(canonical·sitemap·robots·검증 meta·GA 측정ID)은 컨테이너 env 를 고쳐도
 * 반영되지 않는다 — 반드시 build-arg 를 바꾸고 재빌드해야 한다.
 */

const DEFAULT_BASE_URL = "http://localhost:3000"

/** 안과 분석 검토본은 명시적으로 켠 빌드에서만 노출한다. */
export const isLasikEnabled = (): boolean => process.env.NEXT_PUBLIC_LASIK_ENABLED === "true"

/** 공개 여부는 빌드 시 고정한다. 누락·오타는 비공개로 처리하고 개발 배포만 명시적으로 켠다. */
export const isShurinkEnabled = (): boolean => process.env.NEXT_PUBLIC_SHURINK_ENABLED === "true"

/** 리쥬란 검토본은 명시적으로 켠 개발 빌드에만 노출한다. */
export const isRejuranEnabled = (): boolean => process.env.NEXT_PUBLIC_REJURAN_ENABLED === "true"

/** 뒤 슬래시를 떼 절대 URL 을 조립할 때 `//` 가 생기지 않게 한다. */
const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, "")

/** canonical, OG, sitemap 등 절대 URL 생성 기준이 되는 서비스 주소. */
export const getPublicBaseUrl = (): string =>
  stripTrailingSlash(process.env.NEXT_PUBLIC_BASE_URL?.trim() || DEFAULT_BASE_URL)

/** 브라우저에서 호출하는 API 주소. */
export const getPublicApiUrl = (): string =>
  process.env.NEXT_PUBLIC_API_URL?.trim() ||
  (getPublicBaseUrl() === "https://dev.si-sun.com" ? "https://dev.si-sun.com/backend" : "")

/** 서버(route handler, server component) 전용 API 주소. 없으면 public 값으로 폴백한다. */
export const getServerApiUrl = (): string => process.env.API_URL?.trim() || getPublicApiUrl()

/**
 * 색인 게이트 마스터 스위치.
 *
 * `off` 로 빌드하면 모든 페이지에 noindex 가 붙고 sitemap 을 비운다(크롤은 허용).
 * 콘텐츠·의료광고 심의가 흔들릴 때 코드 수정 없이 build-arg 하나로 사이트 전체를 닫기 위한 장치다.
 * 미지정(기본) = 개방.
 */
export const isIndexingEnabled = (): boolean =>
  (process.env.NEXT_PUBLIC_SEO_INDEXING?.trim().toLowerCase() || "on") !== "off"

/** Google Search Console 소유확인 토큰(HTML meta 방식). 도메인 속성은 DNS TXT 라 비워둬도 된다. */
export const getGoogleSiteVerification = (): string => process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION?.trim() || ""

/** 네이버 서치어드바이저 소유확인 토큰. */
export const getNaverSiteVerification = (): string => process.env.NEXT_PUBLIC_NAVER_SITE_VERIFICATION?.trim() || ""

/** Bing 웹마스터 소유확인 토큰. ChatGPT 인용이 Bing 인덱스를 경유하므로 함께 관리한다. */
export const getBingSiteVerification = (): string => process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION?.trim() || ""

/** GA4 측정 ID(G-XXXXXXXXXX). 비어 있으면 태그를 아예 싣지 않는다. */
export const getGaMeasurementId = (): string => process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() || ""

/** 다국어 색인은 명시적으로 on 인 빌드에서만 허용한다. 마스터 게이트와 함께 판정한다. */
export const isI18nIndexingEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_I18N_INDEXING?.trim().toLowerCase() === "on"

/** 인터뷰 프로토타입은 명시적으로 켠 개발 빌드에만 공개한다. */
export const isInterviewEnabled = (): boolean => process.env.NEXT_PUBLIC_INTERVIEW_ENABLED === "true"

/** 개발 인터뷰 API의 서버 주소. 세션 경로(/api/sessions)는 호출부에서 붙인다. */
export const getInterviewApiBase = (): string =>
  stripTrailingSlash(process.env.NEXT_PUBLIC_INTERVIEW_API_BASE?.trim() || "")
